/**
 * Tenant Feature Flags Page
 *
 * Admin UI for per-tenant flags. The backend resolves the tenant from its own
 * stage, so this page always edits one tenant.
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import {
  AlertCircle,
  Pencil,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
  Users,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  FlagCreateDialog,
  FlagGatesPanel,
  FlagMetadataDialog,
  FlagRowIdentity,
  FlagRulesPanel,
  type FlagMetadataBody,
} from '@/components/feature-flags';
import { useArchiveFeatureFlag, useCreateFeatureFlag } from '@/hooks/use-feature-flags';
import { useTenantFeatureFlags, useUpdateTenantFeatureFlag } from '@/hooks/use-tenant-feature-flags';
import { useToast } from '@/hooks/use-toast';
import { env } from '@/config/env';
import { requiresConfirmation } from '@/lib/api-env';
import { TENANT_FLAG_CONFIG_FIELDS } from '@/types/tenant-feature-flag';
import {
  FLAG_CONFIRM_ON_ENABLE,
  getEnforcedFlagDisableCopy,
  PLATFORM_TAG_COPY,
} from '@/types/feature-flag';
import type {
  ConfigFieldDescriptor,
  TenantFeatureFlagState,
  UpdateTenantFeatureFlagBody,
} from '@/types/tenant-feature-flag';
import type { CreateFeatureFlagBody, FeatureFlagDefinition } from '@/types/feature-flag';

interface PendingChange {
  key: string;
  body: UpdateTenantFeatureFlagBody;
  description: string;
  onSaved?: () => void;
}

interface PendingToggle {
  definition: FeatureFlagDefinition;
  nextValue: boolean;
}

const EMPTY_STATE: TenantFeatureFlagState = {
  enabled: false,
  configJson: null,
  updatedBy: null,
  updatedAt: null,
};

export default function TenantFeatureFlagsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useTenantFeatureFlags();
  const updateMutation = useUpdateTenantFeatureFlag();
  const createMutation = useCreateFeatureFlag();
  const archiveMutation = useArchiveFeatureFlag();
  const { toast } = useToast();

  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [pendingToggle, setPendingToggle] = useState<PendingToggle | null>(null);
  const [pendingArchive, setPendingArchive] = useState<FeatureFlagDefinition | null>(null);
  const [pendingGatesFor, setPendingGatesFor] = useState<FeatureFlagDefinition | null>(null);
  const [pendingRulesFor, setPendingRulesFor] = useState<FeatureFlagDefinition | null>(null);
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingMetadataFor, setPendingMetadataFor] = useState<FeatureFlagDefinition | null>(null);
  // Its own error, not updateMutation.error: toggles and config saves share
  // that mutation, so their failures would otherwise surface in this editor.
  const [metadataError, setMetadataError] = useState<Error | null>(null);

  const prodGuard = requiresConfirmation(env.VITE_API_URL);

  // A set, not a single key: two flags can be in flight at once, and one
  // settling must not unlock the other's switch mid-request.
  const markPending = (key: string) => setPendingKeys(prev => new Set(prev).add(key));
  const clearPending = (key: string) =>
    setPendingKeys(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  const openMetadata = (definition: FeatureFlagDefinition) => {
    setMetadataError(null);
    setPendingMetadataFor(definition);
  };

  // The dialog has already run the prod guard, so this writes directly rather
  // than through requestChange, which would confirm a second time.
  const handleMetadataSave = (body: FlagMetadataBody) => {
    if (!pendingMetadataFor) return;
    const key = pendingMetadataFor.key;
    setMetadataError(null);
    markPending(key);
    updateMutation.mutate(
      { key, ...body },
      {
        onSuccess: () => {
          setPendingMetadataFor(null);
          toast({
            title: 'Flag details updated',
            description: `${body.label} now reads as ${PLATFORM_TAG_COPY[body.platformTag].label}.`,
          });
        },
        onError: err => setMetadataError(err instanceof Error ? err : new Error('Unknown error')),
        onSettled: () => clearPending(key),
      }
    );
  };

  const requestChange = (
    key: string,
    body: UpdateTenantFeatureFlagBody,
    description: string,
    onSaved?: () => void
  ) => {
    if (prodGuard) {
      setPendingChange({ key, body, description, onSaved });
      return;
    }
    applyChange(key, body, description, onSaved);
  };

  const applyChange = (
    key: string,
    body: UpdateTenantFeatureFlagBody,
    description: string,
    onSaved?: () => void
  ) => {
    markPending(key);
    updateMutation.mutate(
      { key, ...body },
      {
        onSuccess: () => {
          toast({ title: 'Tenant feature flag updated', description });
          onSaved?.();
        },
        onError: err =>
          toast({
            title: 'Update failed',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          }),
        onSettled: () => clearPending(key),
      }
    );
  };

  // Mirrors the global page exactly: two independent guards on the main
  // toggle, never both live on today's data — enabling a FLAG_CONFIRM_ON_ENABLE
  // key, or disabling an *enforced* key (blocks traffic immediately).
  const requestToggle = (definition: FeatureFlagDefinition, nextValue: boolean) => {
    const needsEnableConfirm = nextValue && FLAG_CONFIRM_ON_ENABLE[definition.key] !== undefined;
    const needsDisableConfirm = !nextValue && definition.enforced;
    if (needsEnableConfirm || needsDisableConfirm || prodGuard) {
      setPendingToggle({ definition, nextValue });
      return;
    }
    applyChange(
      definition.key,
      { enabled: nextValue },
      `${definition.label} set to ${nextValue ? 'ON' : 'OFF'}`
    );
  };

  const handleCreate = (body: CreateFeatureFlagBody) => {
    createMutation.mutate(
      { scope: 'tenant', body },
      {
        onSuccess: definition => {
          toast({
            title: 'Tenant feature flag created',
            description: `${definition.label} is available to target. Admin-created tenant flags are boolean-only.`,
          });
          setCreateOpen(false);
        },
        onError: err =>
          toast({
            title: 'Could not create the flag',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          }),
      }
    );
  };

  const handleArchive = () => {
    if (!pendingArchive) return;
    archiveMutation.mutate(
      { scope: 'tenant', key: pendingArchive.key },
      {
        onSuccess: () => {
          toast({ title: 'Tenant feature flag archived' });
          setPendingArchive(null);
        },
        onError: err =>
          toast({
            title: 'Could not archive the flag',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          }),
      }
    );
  };

  const toggleConfirmCopy = pendingToggle
    ? pendingToggle.nextValue
      ? FLAG_CONFIRM_ON_ENABLE[pendingToggle.definition.key]
      : pendingToggle.definition.enforced
        ? {
            title: `Turn off ${pendingToggle.definition.label}?`,
            description: `This flag is enforced by the backend — it ${getEnforcedFlagDisableCopy(pendingToggle.definition.key)}`,
          }
        : undefined
    : undefined;

  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Environment</AlertTitle>
        <AlertDescription>
          You are editing flags on the tenant resolved by{' '}
          <code className="font-mono">{env.VITE_API_URL}</code>. Confirm you are on the right
          environment before changing anything that affects production.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>
              Tenant Feature Flags
              {data?.tenantKey && (
                <span className="ml-2 rounded bg-muted px-2 py-0.5 font-mono text-xs font-normal">
                  {data.tenantKey}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Per-tenant toggles read by the backend at request time. &quot;Blocks traffic&quot;
              flags reject platform/version overrides — see each row's badge.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh tenant feature flags"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              Create flag
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Failed to load tenant flags</AlertTitle>
              <AlertDescription>
                {error instanceof Error ? error.message : 'Unknown error'}
              </AlertDescription>
            </Alert>
          )}

          {data && data.definitions.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No tenant feature flags yet. Create one to get started.
            </p>
          )}

          {data && (
            <div className="space-y-4">
              {data.definitions.map(definition => (
                <FlagCard
                  key={definition.key}
                  definition={definition}
                  state={data.flags[definition.key] ?? { ...EMPTY_STATE, enabled: definition.defaultEnabled }}
                  onRequestToggle={requestToggle}
                  onRequestChange={requestChange}
                  onRequestArchive={() => setPendingArchive(definition)}
                  onRequestGates={() => setPendingGatesFor(definition)}
                  onRequestRules={() => setPendingRulesFor(definition)}
                  onRequestMetadata={() => openMetadata(definition)}
                  isPending={pendingKeys.has(definition.key)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <FlagMetadataDialog
        definition={pendingMetadataFor}
        prodGuard={prodGuard}
        busy={pendingMetadataFor !== null && pendingKeys.has(pendingMetadataFor.key)}
        error={metadataError}
        onSubmit={handleMetadataSave}
        onClose={() => setPendingMetadataFor(null)}
      />

      <FlagRulesPanel
        scope="tenant"
        definition={pendingRulesFor}
        onClose={() => setPendingRulesFor(null)}
      />

      <FlagGatesPanel
        scope="tenant"
        definition={pendingGatesFor}
        onClose={() => setPendingGatesFor(null)}
      />

      <FlagCreateDialog
        open={createOpen}
        scopeLabel="tenant"
        isSubmitting={createMutation.isPending}
        onSubmit={handleCreate}
        onClose={() => setCreateOpen(false)}
      />

      <AlertDialog
        open={pendingToggle !== null}
        onOpenChange={open => {
          if (!open) setPendingToggle(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{toggleConfirmCopy ? toggleConfirmCopy.title : 'Confirm change'}</AlertDialogTitle>
            <AlertDialogDescription>
              {toggleConfirmCopy ? (
                <>
                  {toggleConfirmCopy.description}
                  {prodGuard && (
                    <span className="mt-2 block">
                      You are making this change against{' '}
                      <code className="font-mono">{env.VITE_API_URL}</code>. This URL is not
                      recognized as a non-prod environment, so the change may affect live users
                      immediately.
                    </span>
                  )}
                </>
              ) : (
                <>
                  You are about to set <strong>{pendingToggle?.definition.label ?? ''}</strong> to{' '}
                  <strong>{pendingToggle?.nextValue ? 'ON' : 'OFF'}</strong> against{' '}
                  <code className="font-mono">{env.VITE_API_URL}</code>. This URL is not recognized
                  as a non-prod environment, so the change may affect live users immediately.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingToggle) {
                  applyChange(
                    pendingToggle.definition.key,
                    { enabled: pendingToggle.nextValue },
                    `${pendingToggle.definition.label} set to ${pendingToggle.nextValue ? 'ON' : 'OFF'}`
                  );
                  setPendingToggle(null);
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingChange !== null}
        onOpenChange={open => {
          if (!open) setPendingChange(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm change</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingChange?.description}. The target backend{' '}
              <code className="font-mono">{env.VITE_API_URL}</code> is not recognized as a non-prod
              environment, so the change may affect live users immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingChange) {
                  applyChange(
                    pendingChange.key,
                    pendingChange.body,
                    pendingChange.description,
                    pendingChange.onSaved
                  );
                  setPendingChange(null);
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingArchive !== null}
        onOpenChange={open => {
          // Same guard as the create/gate dialogs: Escape must not dismiss a
          // confirmation whose request is still in flight.
          if (!open && !archiveMutation.isPending) setPendingArchive(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {pendingArchive?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              The flag stops being served and disappears from this list. Its stored value for this
              tenant is cleared, but the definition and audit history are kept — creating the same
              key again revives it.
              {prodGuard && (
                <span className="mt-2 block">
                  You are archiving against <code className="font-mono">{env.VITE_API_URL}</code>.
                  This URL is not recognized as a non-prod environment, so clients stop receiving
                  this flag immediately.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={event => {
                event.preventDefault();
                handleArchive();
              }}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface FlagCardProps {
  definition: FeatureFlagDefinition;
  state: TenantFeatureFlagState;
  onRequestToggle: (definition: FeatureFlagDefinition, nextValue: boolean) => void;
  onRequestChange: (
    key: string,
    body: UpdateTenantFeatureFlagBody,
    description: string,
    onSaved?: () => void
  ) => void;
  onRequestArchive: () => void;
  onRequestGates: () => void;
  onRequestRules: () => void;
  onRequestMetadata: () => void;
  isPending: boolean;
}

function FlagCard({
  definition,
  state,
  onRequestToggle,
  onRequestChange,
  onRequestArchive,
  onRequestGates,
  onRequestRules,
  onRequestMetadata,
  isPending,
}: FlagCardProps) {
  const configFields = TENANT_FLAG_CONFIG_FIELDS[definition.key] ?? [];

  return (
    <div className="rounded-md border">
      <div className="flex items-start justify-between gap-4 p-4">
        <FlagRowIdentity definition={definition} />
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={state.enabled}
            onCheckedChange={next => onRequestToggle(definition, next)}
            disabled={isPending}
            aria-label={`Toggle ${definition.label}`}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={onRequestMetadata}
            disabled={isPending}
            aria-label={`Edit details for ${definition.label}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={onRequestRules}
            disabled={isPending}
            aria-label={`Identity targeting for ${definition.label}`}
          >
            <Users className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={onRequestGates}
            disabled={isPending}
            aria-label={`Platform and version overrides for ${definition.label}`}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
          {/* Built-ins can be gated (see the icon above) but not archived. */}
          {!definition.isBuiltIn && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-destructive"
              onClick={onRequestArchive}
              aria-label={`Archive ${definition.label}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {configFields.length > 0 && (
        <ConfigForm
          flagKey={definition.key}
          configFields={configFields}
          configJson={state.configJson}
          onRequestChange={onRequestChange}
          isPending={isPending}
        />
      )}

      <AuditFooter updatedBy={state.updatedBy} updatedAt={state.updatedAt} />
    </div>
  );
}

interface ConfigFormProps {
  flagKey: string;
  configFields: ConfigFieldDescriptor[];
  configJson: Record<string, unknown> | null;
  onRequestChange: (
    key: string,
    body: UpdateTenantFeatureFlagBody,
    description: string,
    onSaved?: () => void
  ) => void;
  isPending: boolean;
}

function ConfigForm({
  flagKey,
  configFields,
  configJson,
  onRequestChange,
  isPending,
}: ConfigFormProps) {
  const formSchema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          configFields.map(field => {
            let validator = z
              .number({ invalid_type_error: `${field.label} is required` })
              .int(`${field.label} must be an integer`);
            if (field.min !== undefined) {
              validator = validator.min(field.min, `${field.label} must be at least ${field.min}`);
            }
            if (field.max !== undefined) {
              validator = validator.max(field.max, `${field.label} must be at most ${field.max}`);
            }
            return [field.key, validator] as const;
          })
        )
      ),
    [configFields]
  );
  type FormValues = z.infer<typeof formSchema>;

  const defaultValues = useMemo(
    () =>
      configFields.reduce<Record<string, number | undefined>>((acc, field) => {
        const stored = configJson?.[field.key];
        acc[field.key] = typeof stored === 'number' ? stored : undefined;
        return acc;
      }, {}),
    [configFields, configJson]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues as FormValues,
  });

  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues as FormValues);
    }
  }, [defaultValues, isDirty, reset]);

  const onSubmit = (values: FormValues) => {
    const summary = configFields.map(f => `${f.label} to ${values[f.key]}`).join(', ');
    onRequestChange(flagKey, { configJson: values }, summary, () => reset(values));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 border-t bg-muted/30 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {configFields.map(field => (
          <div key={field.key} className="space-y-1">
            <Label htmlFor={`${flagKey}-${field.key}`} className="text-xs font-medium">
              {field.label}
            </Label>
            <Input
              id={`${flagKey}-${field.key}`}
              type="number"
              min={field.min}
              max={field.max}
              {...register(field.key, { valueAsNumber: true })}
              disabled={isPending}
              aria-invalid={Boolean(errors[field.key])}
            />
            {field.helpText && !errors[field.key] && (
              <p className="text-xs text-muted-foreground">{field.helpText}</p>
            )}
            {errors[field.key] && (
              <p className="text-xs text-destructive">
                {String(errors[field.key]?.message ?? 'Invalid value')}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={!isDirty || isPending}>
          Save config
        </Button>
      </div>
    </form>
  );
}

interface AuditFooterProps {
  updatedBy: string | null;
  updatedAt: string | null;
}

function AuditFooter({ updatedBy, updatedAt }: AuditFooterProps) {
  let formattedAt = '—';
  if (updatedAt) {
    const parsed = new Date(updatedAt);
    if (!Number.isNaN(parsed.getTime())) {
      formattedAt = format(parsed, 'PPpp');
    }
  }
  return (
    <div className="border-t px-4 py-2 text-xs text-muted-foreground">
      Last updated by <span className="font-mono">{updatedBy ?? '—'}</span> on{' '}
      <span>{formattedAt}</span>
    </div>
  );
}
